import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, defineProperty as _defineProperty, assertThisInitialized as _assertThisInitialized, toConsumableArray as _toConsumableArray, createClass as _createClass } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { withContext, eventBus, getMarginBottomClass, marginBottomLevels } from '@piggybank/core';
import { Consumer } from '../Field/context.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import pick from 'lodash/fp/pick';
import Label from '../Label/Label.js';
import { Radio } from '../Radio/Radio.js';
import pull from 'lodash/fp/pull';
import memoize from 'lodash/fp/memoize';
import styles from './tile-group.css.js';
var TileInput = React.forwardRef(function (_ref, ref) {
  var name = _ref.name,
      id = _ref.id,
      value = _ref.value,
      isCheckbox = _ref.isCheckbox,
      selected = _ref.selected,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      invalid = _ref.invalid,
      rest = _objectWithoutProperties(_ref, ["name", "id", "value", "isCheckbox", "selected", "onChange", "onBlur", "invalid"]);

  return isCheckbox ? React.createElement(Checkbox, _extends({
    id: id,
    name: value,
    checked: selected,
    onChange: onChange,
    onBlur: onBlur,
    invalid: invalid,
    ref: ref
  }, rest)) : React.createElement(Radio, _extends({
    id: id,
    name: name,
    value: value,
    selectedValue: selected ? value : '',
    onChange: onChange,
    onBlur: onBlur,
    invalid: invalid,
    ref: ref
  }, rest));
});
TileInput.displayName = 'TileInput';
TileInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  invalid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isCheckbox: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired
};

var TileGroup = /*#__PURE__*/function (_React$Component) {
  _inherits(TileGroup, _React$Component);

  function TileGroup(props) {
    var _this;

    _classCallCheck(this, TileGroup);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TileGroup).call(this, props));

    _defineProperty(_assertThisInitialized(_this), "inputRefs", []);

    _defineProperty(_assertThisInitialized(_this), "keySortOrder", {});

    _defineProperty(_assertThisInitialized(_this), "isCheckbox", false);

    _defineProperty(_assertThisInitialized(_this), "handleBlur", function (e) {
      var newFocusedInput = _this.inputRefs.filter(function (ref) {
        return ref.current === e.relatedTarget;
      });

      if (_this.props.onBlur && newFocusedInput.length === 0) {
        _this.props.onBlur(e);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "renderTile", function (child, index) {
      var _this$props = _this.props,
          name = _this$props.name,
          value = _this$props.value,
          invalid = _this$props.invalid,
          describers = _this$props.describers;
      var tileName = "".concat(name, "-").concat(child.props.value);
      var inputId = "".concat(tileName, "-").concat(_this.isCheckbox ? 'checkbox' : 'radio-item');
      var selected = value.includes(child.props.value);

      var handleChange = _this.getHandleChange(child.props.value);

      var inputProps = {
        id: inputId,
        name: name,
        value: child.props.value,
        isCheckbox: _this.isCheckbox,
        selected: selected,
        onChange: handleChange,
        onBlur: _this.handleBlur,
        invalid: invalid,
        describers: describers,
        ref: _this.inputRefs[index]
      };
      var labelProps = {
        name: tileName,
        htmlFor: inputId
      };
      return React.cloneElement(child, {
        Input: TileInput,
        Label: Label,
        inputProps: inputProps,
        labelProps: labelProps,
        invalid: invalid,
        selected: selected,
        onChange: handleChange
      });
    });

    _this.getInputRefs = memoize(function (n) {
      return _toConsumableArray(new Array(n)).map(function () {
        return React.createRef();
      });
    });
    _this.getHandleChange = memoize(function (key) {
      return function (_ref2) {
        var checked = _ref2.value,
            event = _ref2.event;
        var value;

        if (_this.isCheckbox) {
          if (checked) {
            value = [].concat(_toConsumableArray(_this.props.value), [key]).sort(function (a, b) {
              var itemA = _this.keySortOrder[a];
              var itemB = _this.keySortOrder[b];
              return itemA > itemB ? 1 : itemA < itemB ? -1 : 0;
            });

            _this.props.onChange({
              value: value,
              event: event
            });
          } else {
            value = _toConsumableArray(pull(key, _this.props.value));

            _this.props.onChange({
              value: value,
              event: event
            });
          }
        } else {
          value = [key];

          _this.props.onChange({
            value: value,
            event: event
          });
        }

        eventBus.dispatch({
          type: 'onChange',
          component: 'TileGroup',
          value: value,
          name: _this.props.name
        });
      };
    });
    return _this;
  }

  _createClass(TileGroup, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          children = _this$props2.children,
          marginBottom = _this$props2.marginBottom,
          multiSelect = _this$props2.multiSelect;
      this.isCheckbox = multiSelect || React.Children.count(children) === 1; // We want the output value to be sorted by the order of the tiles in the DOM so we'll keep track of this here

      React.Children.forEach(children, function (child, index) {
        _this2.keySortOrder[child.props.value] = index;
      });
      this.inputRefs = this.getInputRefs(children.length);
      return React.createElement("div", {
        className: cn(styles.TileGroup, getMarginBottomClass(marginBottom))
      }, React.Children.map(children, this.renderTile));
    }
  }]);

  return TileGroup;
}(React.Component);

TileGroup.defaultProps = {
  multiSelect: false,
  marginBottom: 0
};
TileGroup.displayName = 'TileGroup';
TileGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * 0, 1, 2, 3, 4, 5, 6, 7
   */
  marginBottom: PropTypes.oneOf(marginBottomLevels),
  multiSelect: PropTypes.bool,

  /** docgen-from-context:<Fieldset/> */
  invalid: PropTypes.bool,

  /** docgen-from-context:<Fieldset/> */
  describers: PropTypes.string,

  /** docgen-from-context:<Fieldset/> */
  name: PropTypes.string.isRequired,

  /** docgen-from-context:<Fieldset/> */
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,

  /**
   * docgen-from-context:<Fieldset/>
   * ---
   * proxied to eventBus
   */
  onChange: PropTypes.func,

  /** docgen-from-context:<Fieldset/> */
  onBlur: PropTypes.func,
  children: PropTypes.node.isRequired
} : {};
var TileGroup$1 = withContext(Consumer, pick(['name', 'value', 'onChange', 'onBlur', 'invalid', 'describers']))(TileGroup);
export default TileGroup$1;
export { TileGroup };